import { useEffect, useState } from 'react';
import { thumbnailBlobUrl } from '../../api/files.ts';
import { FileIcon } from './FileIcon.tsx';
import type { ApiNode } from '../../api/types.ts';

interface Props {
  namespaceId: string;
  node: ApiNode;
  variant?: 's' | 'm' | 'l';
  size?: number;
}

/**
 * 썸네일 이미지. 서버가 아직 만드는 중이면(202) 잠시 뒤 한 번 더 시도한다.
 * 실패하거나 대상이 아니면 파일 아이콘으로 조용히 대체한다.
 */
export function Thumb({ namespaceId, node, variant = 'm', size = 36 }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!node.has_thumbnail || node.type !== 'file') { setUrl(null); return; }

    let cancelled = false;
    let retry: number | undefined;

    const load = (attempt: number): void => {
      thumbnailBlobUrl(namespaceId, node.path, variant)
        .then((result) => {
          if (cancelled) return;
          if (result === 'generating') {
            // 생성 중이면 한 번만 더 기다린다 (무한 폴링은 목록에서 부담이 크다)
            if (attempt < 1) retry = window.setTimeout(() => { load(attempt + 1); }, 1500);
            else setFailed(true);
            return;
          }
          setUrl(result);
        })
        .catch(() => { if (!cancelled) setFailed(true); });
    };

    load(0);
    return () => { cancelled = true; window.clearTimeout(retry); };
  }, [namespaceId, node.path, node.has_thumbnail, node.type, variant]);

  if (!url || failed) return <FileIcon node={node} size={size} />;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}
